<script setup lang="ts">
import { computed } from 'vue';
import { useConfigStore } from '../store/configStore';

const config      = useConfigStore();
const companyName = computed(() => config.getCompanyName);
const brandMark   = computed(() => (companyName.value.trim()[0] || '•').toUpperCase());
const lastUpdated = '7 July 2026';
const year        = new Date().getFullYear();

const toc = [
  { id: 'intro',       label: 'Introduction' },
  { id: 'definitions', label: 'Definitions' },
  { id: 'accounts',    label: 'Accounts & eligibility' },
  { id: 'customers',   label: 'For Customers' },
  { id: 'partners',    label: 'For Partners' },
  { id: 'drivers',     label: 'For Drivers' },
  { id: 'apps',        label: 'Web & mobile apps' },
  { id: 'payments',    label: 'Payments & fees' },
  { id: 'data',        label: 'Data & privacy' },
  { id: 'liability',   label: 'Liability & indemnity' },
  { id: 'termination', label: 'Suspension & termination' },
  { id: 'changes',     label: 'Changes, law & contact' },
];
</script>

<template>
  <div class="tnc">
    <!-- Top bar -->
    <header class="tnc-bar">
      <router-link to="/login" class="tnc-brand">
        <span class="tnc-mark">{{ brandMark }}</span>
        <span class="tnc-brand-name">{{ companyName }}</span>
      </router-link>
      <router-link to="/login" class="tnc-back">← Back to sign in</router-link>
    </header>

    <div class="tnc-wrap">
      <!-- Title -->
      <div class="tnc-head">
        <h1 class="tnc-title">Terms &amp; Conditions</h1>
        <p class="tnc-sub">Last updated: {{ lastUpdated }}</p>
        <p class="tnc-intro-line">
          These terms govern everyone who uses {{ companyName }} — the customers who book
          deliveries, the partners who move them, and the drivers who fulfil them.
        </p>
      </div>

      <div class="tnc-body">
        <!-- Contents -->
        <nav class="tnc-toc">
          <span class="tnc-toc-label">Contents</span>
          <a v-for="t in toc" :key="t.id" :href="'#' + t.id" class="tnc-toc-link">{{ t.label }}</a>
        </nav>

        <!-- Document -->
        <article class="tnc-doc">
          <section id="intro">
            <h2>1. Introduction</h2>
            <p>
              These Terms &amp; Conditions (the “Terms”) form a binding agreement between you and
              {{ companyName }} (the “Company”, “we”, “us”) and govern your access to and use of our
              websites, mobile applications, and related services (together, the “Platform”).
            </p>
            <p>
              {{ companyName }} is a technology platform that connects <strong>Customers</strong> who
              need goods moved with <strong>Partners</strong> and <strong>Drivers</strong> who provide
              transport and delivery services. By creating an account, accepting these Terms, or using
              the Platform in any capacity, you agree to be bound by them. If you do not agree, do not
              use the Platform.
            </p>
          </section>

          <section id="definitions">
            <h2>2. Definitions</h2>
            <ul>
              <li><strong>Customer</strong> — a person or business that requests a delivery through the Platform.</li>
              <li><strong>Partner</strong> — a transport business or fleet owner that fulfils deliveries, whether directly or through its drivers.</li>
              <li><strong>Driver</strong> — an individual who operates a vehicle to collect and deliver goods via the Platform.</li>
              <li><strong>Order</strong> — a delivery request, including pick-up and drop-off details, vehicle type, and price.</li>
              <li><strong>Services</strong> — the transport, delivery, tracking, and related services made available through the Platform.</li>
            </ul>
          </section>

          <section id="accounts">
            <h2>3. Accounts &amp; eligibility</h2>
            <ul>
              <li>You must be at least 18 years old and able to enter a binding contract.</li>
              <li>You must provide accurate, current information and keep it up to date.</li>
              <li>You are responsible for activity under your account and for keeping your credentials secure.</li>
              <li>We may verify your identity and documents, and may suspend or refuse accounts that breach these Terms or applicable law.</li>
            </ul>
          </section>

          <section id="customers">
            <h2>4. For Customers</h2>
            <p>The following applies when you use the Platform to request deliveries.</p>
            <ul>
              <li><strong>Placing orders.</strong> You must give accurate pick-up and drop-off details, choose the correct vehicle type, and describe the goods truthfully.</li>
              <li><strong>Pricing &amp; payment.</strong> Prices are shown before you confirm and may vary with distance, vehicle, waiting time, and demand. You authorise us (or our payment partners) to charge the applicable fees and any taxes.</li>
              <li><strong>Cancellations &amp; changes.</strong> You may cancel or amend an Order subject to the cancellation terms shown at the time; fees may apply once a Partner or Driver has been assigned or dispatched.</li>
              <li><strong>Availability.</strong> You are responsible for having someone available to release goods at pick-up and receive them at drop-off, and for any access, permits, or packaging required.</li>
              <li><strong>Prohibited &amp; restricted items.</strong> You must not send illegal, hazardous, stolen, or otherwise restricted goods. You remain responsible for the contents and legality of every Order.</li>
              <li><strong>Proof of delivery.</strong> Deliveries may be confirmed by signature, photo, or other proof captured in the app.</li>
            </ul>
          </section>

          <section id="partners">
            <h2>5. For Partners</h2>
            <p>The following applies to transport businesses and fleet owners that fulfil deliveries.</p>
            <ul>
              <li><strong>Independent relationship.</strong> Partners operate as independent contractors. Nothing in these Terms creates an employment, agency, or joint-venture relationship with the Company.</li>
              <li><strong>Eligibility &amp; documentation.</strong> You must hold and maintain all licences, permits, valid insurance, and roadworthy, compliant vehicles, and provide documentation on request.</li>
              <li><strong>Service standards.</strong> You must carry out Orders promptly and professionally, handle goods with care, and keep Order information confidential.</li>
              <li><strong>Your drivers.</strong> You are responsible for the drivers you engage, including their eligibility, conduct, and compliance with these Terms.</li>
              <li><strong>Settlement &amp; payouts.</strong> Earnings, commissions, and any advances are settled through the Platform on the applicable cycle, net of platform fees and adjustments. Statements are available in your account.</li>
              <li><strong>Insurance &amp; liability.</strong> You are responsible for loss of or damage to goods while in your or your drivers’ custody, and must maintain adequate insurance to cover it.</li>
            </ul>
          </section>

          <section id="drivers">
            <h2>6. For Drivers</h2>
            <p>The following applies to individuals who collect and deliver goods through the Platform.</p>
            <ul>
              <li><strong>Eligibility.</strong> You must hold a valid driving licence, be fit to drive, and meet any verification and background requirements.</li>
              <li><strong>App use &amp; availability.</strong> You choose when to go online and which jobs to accept. When online, you agree to receive Orders and to keep your status accurate.</li>
              <li><strong>Location data.</strong> While you are on duty, the app collects your location so Customers and operations can track deliveries in real time. You may go offline to stop sharing your location.</li>
              <li><strong>Conduct &amp; safety.</strong> You must obey all traffic and safety laws, never drive under the influence, and treat customers, goods, and other road users with care and respect.</li>
              <li><strong>Proof of delivery.</strong> You must capture the required proof (photo, signature, or documents) before an Order can be completed.</li>
              <li><strong>Ratings.</strong> Customer feedback and performance metrics may affect your access to Orders on the Platform.</li>
            </ul>
          </section>

          <section id="apps">
            <h2>7. Web &amp; mobile apps</h2>
            <p>The Platform is provided as web applications and mobile apps. The following applies however you access it.</p>
            <ul>
              <li><strong>Licence.</strong> We grant you a limited, non-exclusive, non-transferable, revocable licence to access the web apps and to install and use the mobile apps on devices you own or control, solely to use the Services. You must not copy, modify, reverse-engineer, or redistribute them.</li>
              <li><strong>Device permissions.</strong> The mobile apps may request permissions such as location, camera, notifications, and storage. Driver apps use location — including in the background while on duty — so deliveries can be tracked in real time; the camera is used to capture proof of delivery. You can manage permissions in your device or browser settings, but some features will not work without them.</li>
              <li><strong>App stores.</strong> Where you obtain a mobile app through a third-party store such as Google Play or the App Store, your download and use are also subject to that store’s terms. The store is not a party to these Terms and is not responsible for the apps or Services.</li>
              <li><strong>Updates.</strong> We may release updates that add, change, or remove features. Keeping the app or browser up to date may be necessary for the Services to work correctly.</li>
            </ul>
          </section>

          <section id="payments">
            <h2>8. Payments &amp; fees</h2>
            <p>
              Fees, commissions, and payout terms are shown in the Platform and may change from time to
              time. You are responsible for any taxes that apply to your use of the Services. We may use
              third-party payment providers, and your use of those services may be subject to their terms.
            </p>
          </section>

          <section id="data">
            <h2>9. Data &amp; privacy</h2>
            <p>
              We process personal data — such as contact details, location, order history, and, where
              relevant, driver and vehicle documents — to operate the Platform and deliver the Services.
              Our handling of personal data is described in our Privacy Policy, which forms part of these
              Terms.
            </p>
          </section>

          <section id="liability">
            <h2>10. Liability &amp; indemnity</h2>
            <p>
              The Platform connects Customers, Partners, and Drivers; transport itself is provided by
              Partners and Drivers. To the fullest extent permitted by law, the Platform and Services are
              provided “as is”, and the Company is not liable for indirect or consequential loss. Nothing
              in these Terms excludes liability that cannot lawfully be excluded.
            </p>
            <p>
              You agree to indemnify the Company against claims, losses, and costs arising from your
              breach of these Terms, your Orders or goods, or your violation of any law or third-party
              rights.
            </p>
          </section>

          <section id="termination">
            <h2>11. Suspension &amp; termination</h2>
            <p>
              We may suspend or terminate access to the Platform if you breach these Terms, create risk or
              legal exposure, or use the Services fraudulently or unlawfully. You may stop using the
              Platform and close your account at any time, subject to settling any outstanding amounts.
            </p>
          </section>

          <section id="changes">
            <h2>12. Changes, governing law &amp; contact</h2>
            <p>
              We may update these Terms from time to time. When we make material changes, we will post the
              updated Terms and revise the “last updated” date above; continuing to use the Platform means
              you accept the changes.
            </p>
            <p>
              These Terms are governed by the laws of the jurisdiction in which the Company is established,
              and any disputes are subject to the courts of that jurisdiction, without prejudice to any
              mandatory consumer protections available to you.
            </p>
            <p>
              Questions about these Terms? Contact us through the support channels shown in the Platform or
              on the {{ companyName }} website.
            </p>
          </section>

          <p class="tnc-footer">© {{ year }} {{ companyName }}. All rights reserved.</p>
        </article>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Serif+Display&display=swap');

/* Own scroll container — the app shell sets body { overflow:hidden } and renders
   auth routes without the usual scrollable .main-container wrapper. */
.tnc { height: 100vh; overflow-y: auto; background: #f7f8fa; font-family: 'DM Sans', sans-serif; color: #26303f; }

/* Top bar */
.tnc-bar {
  position: sticky; top: 0; z-index: 10;
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 32px; background: rgba(255,255,255,0.85); backdrop-filter: blur(8px);
  border-bottom: 1px solid #ececf0;
}
.tnc-brand { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.tnc-mark {
  width: 34px; height: 34px; border-radius: 10px; background: #0061eb; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Serif Display', serif; font-size: 1rem;
  box-shadow: 0 5px 14px rgba(0,97,235,0.24);
}
.tnc-brand-name { font-size: 0.95rem; font-weight: 600; color: #0f1c2e; }
.tnc-back { font-size: 0.85rem; color: #0061eb; text-decoration: none; opacity: 0.8; }
.tnc-back:hover { opacity: 1; }

/* Layout */
.tnc-wrap { max-width: 1040px; margin: 0 auto; padding: 44px 32px 80px; }
.tnc-head { margin-bottom: 40px; }
.tnc-title { font-family: 'DM Serif Display', serif; font-weight: 400; font-size: 2.6rem; letter-spacing: -0.02em; color: #0f1c2e; margin: 0 0 8px; }
.tnc-sub { font-size: 0.85rem; color: #9aa1ad; margin: 0 0 18px; }
.tnc-intro-line { font-size: 1.02rem; font-weight: 300; line-height: 1.7; color: #5b6472; margin: 0; max-width: 680px; }

.tnc-body { display: grid; grid-template-columns: 220px 1fr; gap: 48px; align-items: start; }

/* TOC */
.tnc-toc { position: sticky; top: 92px; display: flex; flex-direction: column; gap: 2px; }
.tnc-toc-label { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; color: #9aa1ad; margin-bottom: 10px; }
.tnc-toc-link { font-size: 0.88rem; color: #5b6472; text-decoration: none; padding: 6px 10px; border-radius: 7px; transition: background 0.15s, color 0.15s; }
.tnc-toc-link:hover { background: #eef1f6; color: #0061eb; }

/* Document */
.tnc-doc { max-width: 720px; }
.tnc-doc section { padding-top: 8px; margin-bottom: 34px; scroll-margin-top: 92px; }
.tnc-doc h2 { font-family: 'DM Serif Display', serif; font-weight: 400; font-size: 1.4rem; color: #0f1c2e; margin: 0 0 12px; letter-spacing: -0.01em; }
.tnc-doc p { font-size: 0.95rem; line-height: 1.75; color: #4a5462; font-weight: 300; margin: 0 0 12px; }
.tnc-doc strong { font-weight: 600; color: #26303f; }
.tnc-doc ul { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 12px; }
.tnc-doc li { position: relative; padding-left: 22px; font-size: 0.95rem; line-height: 1.7; color: #4a5462; font-weight: 300; }
.tnc-doc li::before { content: ''; position: absolute; left: 4px; top: 10px; width: 6px; height: 6px; border-radius: 50%; background: #0061eb; opacity: 0.55; }

.tnc-footer { margin-top: 48px; padding-top: 24px; border-top: 1px solid #ececf0; font-size: 0.82rem; color: #9aa1ad; }

@media (max-width: 860px) {
  .tnc-body { grid-template-columns: 1fr; gap: 28px; }
  .tnc-toc { position: static; flex-direction: row; flex-wrap: wrap; gap: 6px; padding-bottom: 8px; border-bottom: 1px solid #ececf0; }
  .tnc-toc-label { width: 100%; }
  .tnc-toc-link { background: #eef1f6; }
  .tnc-title { font-size: 2.1rem; }
}
</style>
